import { Currency, CurrencyAmount, Percent, Token } from '@kodiak-finance/sdk-core'
import { FeeAmount, NonfungiblePositionManager } from '@kodiak-finance/v3-sdk'
import { useQueryClient } from '@tanstack/react-query'
import AdvancedDeploySection from 'components/DeployIslandPermissionless'
import Loader from 'components/Loader'
import PoweredByKodiak from 'components/PoweredByKodiak'
import UnsupportedCurrencyFooter from 'components/swap/UnsupportedCurrencyFooter'
import { SupportedChainId } from 'config/chains'
import { v3QueryKeys } from 'hooks/subgraph/v3'
import { useChainAwareNavigate } from 'hooks/useChainAwareNavigate'
import { useConnection } from 'hooks/useConnection'
import { useCurrencyId } from 'hooks/useCurrencyId'
import { useKodiakVault } from 'hooks/useKodiakVault'
import useParsedQueryString from 'hooks/useParsedQueryString'
import { useResolveCurrencyAddress } from 'hooks/useResolveCurrencyAddress'
import { useApiTokenValue } from 'hooks/useTokenPrice'
import { useSendTransaction } from 'hooks/useTransaction'
import useWaitForTransactionReceipt from 'hooks/useWaitForTransactionReceipt'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle } from 'react-feather'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Text } from 'rebass'
import {
  usePresetRanges,
  useRangeHopCallbacks,
  useV3DerivedMintInfo,
  useV3MintActionHandlers,
  useV3MintState,
} from 'state/mint/v3/hooks'
import { ThemeContext } from 'styled-components'
import { Address, Hex } from 'viem'

import { ButtonError, ButtonLight, ButtonPrimary, ButtonText, ButtonYellow } from '../../components/Button'
import { BlueCard, YellowCard } from '../../components/Card'
import { AutoColumn } from '../../components/Column'
import CurrencyInputPanel from '../../components/CurrencyInputPanel'
import FeeSelector from '../../components/FeeSelector'
import HoverInlineText from '../../components/HoverInlineText'
import LiquidityChartRangeInput from '../../components/LiquidityChartRangeInput'
import { AddRemoveTabs } from '../../components/NavigationTabs'
import { PositionPreview } from '../../components/PositionPreview'
import RangeSelector from '../../components/RangeSelector'
import PresetsButtons from '../../components/RangeSelector/PresetsButtons'
import RateToggle from '../../components/RateToggle'
import Row, { AutoRow, RowBetween, RowFixed } from '../../components/Row'
import { SwitchLocaleLink } from '../../components/SwitchLocaleLink'
import TransactionConfirmationModal, {
  ConfirmationModalContent,
  TransactionErrorContent,
} from '../../components/TransactionConfirmationModal'
import { NONFUNGIBLE_POSITION_MANAGER_ADDRESSES } from '../../constants/addresses'
import { ZERO_ADDRESS, ZERO_PERCENT } from '../../constants/misc'
import { WRAPPED_NATIVE_CURRENCY } from '../../constants/tokens'
import { useCurrency } from '../../hooks/Tokens'
import { ApprovalState, useApproveCallback } from '../../hooks/useApproveCallback'
import { useDerivedPositionInfo } from '../../hooks/useDerivedPositionInfo'
import { useIsSwapUnsupported } from '../../hooks/useIsSwapUnsupported'
import { useNativeCurrencyPreference } from '../../hooks/useNativeCurrencyPreference'
import useTransactionDeadline from '../../hooks/useTransactionDeadline'
import { useV3PositionFromTokenId } from '../../hooks/useV3Positions'
import { useToggleWalletModal } from '../../state/application/hooks'
import { Bound, Field, PresetRange, setPresetRange } from '../../state/mint/v3/actions'
import { useTransactionAdder } from '../../state/transactions/hooks'
import { TransactionType } from '../../state/transactions/types'
import { useUserSlippageToleranceWithDefault } from '../../state/user/hooks'
import { ThemedText } from '../../theme'
import { calculateGasMargin } from '../../utils/calculateGasMargin'
import { currencyId, isNativeCurrencyId } from '../../utils/currencyId'
import { maxAmountSpend } from '../../utils/maxAmountSpend'
import { checkErrorMessage } from '../../utils/transactionErrors'
import { Dots } from '../Pool/styleds'
import { Review } from './Review'
import {
  CurrencyDropdown,
  DynamicSection,
  HideMedium,
  MediumOnly,
  PageWrapper,
  ResponsiveTwoColumns,
  RightContainer,
  ScrollablePage,
  SelectPairFlex,
  StackedContainer,
  StackedItem,
  StyledInput,
  StyledOutlineCard,
  Wrapper,
} from './styled'

const DEFAULT_ADD_IN_RANGE_SLIPPAGE_TOLERANCE = new Percent(50, 10_000)

interface TransactionState {
  attemptingTxn: boolean
  errorMessage: string | undefined
  txHash: string | undefined
  transactionWaiting?: boolean
}

export default function AddLiquidity() {
  const navigate = useChainAwareNavigate()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  const hydratedLeftRef = useRef(false)
  const hydratedRightRef = useRef(false)

  const {
    currencyIdA,
    currencyIdB,
    feeAmount: feeAmountFromUrl,
    tokenId,
  } = useParams<{ currencyIdA?: string; currencyIdB?: string; feeAmount?: string; tokenId?: string }>()

  const { deployVault } = useKodiakVault()
  const { account, chainId, wagmiPublicClient } = useConnection()
  const theme = useContext(ThemeContext)
  const toggleWalletModal = useToggleWalletModal() // toggle wallet when disconnected
  const addTransaction = useTransactionAdder()
  const parsedQs = useParsedQueryString()
  const { sendTransactionAsync } = useSendTransaction()
  const waitForTransactionReceipt = useWaitForTransactionReceipt()
  const [receiveWETH, setReceiveWETH] = useState(false)
  const { nativeCurrency, getCurrencyWithPreference } = useNativeCurrencyPreference(receiveWETH)

  const { position: existingPositionDetails, loading: positionLoading } = useV3PositionFromTokenId(
    tokenId ? tokenId : undefined
  )
  const isCheckingExisting = Boolean(tokenId) && positionLoading
  const hasExistingPosition = !!existingPositionDetails && !positionLoading
  const { position: existingPosition } = useDerivedPositionInfo(existingPositionDetails)

  // fee selection from url
  const feeAmount: FeeAmount | undefined =
    feeAmountFromUrl && Object.values(FeeAmount).includes(parseFloat(feeAmountFromUrl))
      ? parseFloat(feeAmountFromUrl)
      : undefined

  const _currencyIdA = useResolveCurrencyAddress(currencyIdA)
  const _currencyIdB = useResolveCurrencyAddress(currencyIdB)

  const baseCurrency = useCurrency(_currencyIdA)
  const currencyB = useCurrency(_currencyIdB)
  // prevent an error if they input ETH/WETH
  const quoteCurrency =
    baseCurrency && currencyB && baseCurrency.wrapped.equals(currencyB.wrapped) ? undefined : currencyB
  const baseCurrencyWithPreference = useMemo(
    () => (baseCurrency ? getCurrencyWithPreference(baseCurrency) : undefined),
    [baseCurrency, getCurrencyWithPreference]
  )
  const quoteCurrencyWithPreference = useMemo(
    () => (quoteCurrency ? getCurrencyWithPreference(quoteCurrency) : undefined),
    [getCurrencyWithPreference, quoteCurrency]
  )

  const baseCurrencyId = useCurrencyId(baseCurrency)
  const quoteCurrencyId = useCurrencyId(quoteCurrency)
  // mint state
  const {
    independentField,
    typedValue,
    startPriceTypedValue,
    rightRangeTypedValue,
    leftRangeTypedValue,
    leftRangeTick,
    rightRangeTick,
    selectedPreset,
  } = useV3MintState()

  const {
    pool,
    ticks,
    dependentField,
    price,
    pricesAtTicks,
    parsedAmounts,
    currencyBalances,
    position,
    noLiquidity,
    currencies,
    errorMessage,
    invalidPool,
    invalidRange,
    outOfRange,
    depositADisabled,
    depositBDisabled,
    invertPrice,
    ticksAtLimit,
    token0: sorted0,
    token1: sorted1,
  } = useV3DerivedMintInfo(
    baseCurrencyWithPreference,
    quoteCurrencyWithPreference,
    feeAmount,
    baseCurrencyWithPreference,
    existingPosition
  )

  const { applyPreset } = usePresetRanges({
    baseCurrency: baseCurrencyWithPreference,
    quoteCurrency: quoteCurrencyWithPreference,
    feeAmount,
    currentPrice: price,
    noLiquidity,
    invertPrice,
  })

  const handlePresetSelect = useCallback(
    (preset: PresetRange) => {
      applyPreset(preset)
    },
    [applyPreset]
  )

  const {
    onFieldAInput,
    onFieldBInput,
    onLeftRangeInput,
    onRightRangeInput,
    onStartPriceInput,
    onLeftRangeTickInput,
    onRightRangeTickInput,
  } = useV3MintActionHandlers(noLiquidity, sorted0, sorted1, feeAmount, invertPrice)

  const isValid = !errorMessage && !invalidRange

  const [transactionState, setTransactionState] = useState<TransactionState>({
    attemptingTxn: false,
    errorMessage: undefined,
    txHash: undefined,
  })

  // modal and loading
  const [showConfirm, setShowConfirm] = useState<boolean>(false)

  // capital efficiency warning
  const [showCapitalEfficiencyWarning, setShowCapitalEfficiencyWarning] = useState(false)

  useEffect(() => setShowCapitalEfficiencyWarning(false), [baseCurrency, quoteCurrency, feeAmount])

  const prevPairKeyRef = useRef<string | undefined>(undefined)
  const pendingPresetRef = useRef<boolean>(false)
  const autoAppliedWidePresetRef = useRef<boolean>(false)

  const pairKey = useMemo(() => {
    const a = baseCurrency?.wrapped?.address?.toLowerCase()
    const b = quoteCurrency?.wrapped?.address?.toLowerCase()
    return a && b && feeAmount !== undefined ? `${a}-${b}-${feeAmount}` : 'unset'
  }, [baseCurrency, quoteCurrency, feeAmount])

  useEffect(() => {
    if (!pairKey) return

    const changed = prevPairKeyRef.current && prevPairKeyRef.current !== pairKey
    prevPairKeyRef.current = pairKey

    if (changed) {
      pendingPresetRef.current = true
      autoAppliedWidePresetRef.current = false
    }

    if (
      pendingPresetRef.current &&
      price &&
      selectedPreset !== PresetRange.FULL &&
      selectedPreset !== PresetRange.CUSTOM
    ) {
      pendingPresetRef.current = false
      handlePresetSelect(selectedPreset)
    }
  }, [pairKey, price, selectedPreset, baseCurrency, quoteCurrency, feeAmount])

  useEffect(() => {
    if (selectedPreset !== PresetRange.FULL) return

    if (
      !hydratedLeftRef.current &&
      typeof parsedQs.minPrice === 'string' &&
      parsedQs.minPrice.length > 0 &&
      !isNaN(parsedQs.minPrice as any)
    ) {
      hydratedLeftRef.current = true
      onLeftRangeInput(parsedQs.minPrice, { suppressUrl: true })
    }

    if (
      !hydratedRightRef.current &&
      typeof parsedQs.maxPrice === 'string' &&
      parsedQs.maxPrice.length > 0 &&
      !isNaN(parsedQs.maxPrice as any)
    ) {
      hydratedRightRef.current = true
      onRightRangeInput(parsedQs.maxPrice, { suppressUrl: true })
    }
  }, [parsedQs, onLeftRangeInput, onRightRangeInput])

  useEffect(() => {
    if (autoAppliedWidePresetRef.current) return
    if (selectedPreset !== PresetRange.FULL) return
    if (hasExistingPosition) return
    if (noLiquidity) return
    if (!baseCurrency || !quoteCurrency || !feeAmount || !price) return

    const hasRangeQuery =
      (typeof parsedQs.minPrice === 'string' && parsedQs.minPrice.trim().length > 0) ||
      (typeof parsedQs.maxPrice === 'string' && parsedQs.maxPrice.trim().length > 0)

    if (hasRangeQuery) return

    autoAppliedWidePresetRef.current = true
    handlePresetSelect(PresetRange.WIDE)
  }, [
    selectedPreset,
    hasExistingPosition,
    noLiquidity,
    baseCurrency,
    quoteCurrency,
    feeAmount,
    price,
    parsedQs,
    handlePresetSelect,
  ])

  const deadline = useTransactionDeadline() // custom from users settings

  // get formatted amounts
  const formattedAmounts = {
    [independentField]: typedValue,
    [dependentField]: parsedAmounts[dependentField]?.toSignificant(6) ?? '',
  }

  const convertToTokenAmount = (amount: CurrencyAmount<Currency> | undefined | null): CurrencyAmount<Token> | null => {
    if (!amount) return null

    if (amount.currency.isToken) {
      return amount as CurrencyAmount<Token>
    }

    if (amount.currency.isNative) {
      try {
        const wrappedToken = amount.currency.wrapped
        return CurrencyAmount.fromRawAmount(wrappedToken, amount.quotient)
      } catch (error) {
        console.error('Error converting native currency to wrapped token:', error)
        return null
      }
    }

    return null
  }

  const usdcValues = {
    [Field.CURRENCY_A]: useApiTokenValue(convertToTokenAmount(parsedAmounts[Field.CURRENCY_A])).value,
    [Field.CURRENCY_B]: useApiTokenValue(convertToTokenAmount(parsedAmounts[Field.CURRENCY_B])).value,
  }

  // get the max amounts user can add
  const maxAmounts: { [field in Field]?: CurrencyAmount<Currency> } = [Field.CURRENCY_A, Field.CURRENCY_B].reduce(
    (accumulator, field) => {
      return {
        ...accumulator,
        [field]: maxAmountSpend(currencyBalances[field]),
      }
    },
    {}
  )

  const atMaxAmounts: { [field in Field]?: CurrencyAmount<Currency> } = [Field.CURRENCY_A, Field.CURRENCY_B].reduce(
    (accumulator, field) => {
      return {
        ...accumulator,
        [field]: maxAmounts[field]?.equalTo(parsedAmounts[field] ?? '0'),
      }
    },
    {}
  )

  // check whether the user has approved the router on the tokens
  const [approvalA, approveACallback] = useApproveCallback(
    parsedAmounts[Field.CURRENCY_A],
    chainId ? NONFUNGIBLE_POSITION_MANAGER_ADDRESSES[chainId] : undefined
  )
  const [approvalB, approveBCallback] = useApproveCallback(
    parsedAmounts[Field.CURRENCY_B],
    chainId ? NONFUNGIBLE_POSITION_MANAGER_ADDRESSES[chainId] : undefined
  )

  const allowedSlippage = useUserSlippageToleranceWithDefault(
    outOfRange ? ZERO_PERCENT : DEFAULT_ADD_IN_RANGE_SLIPPAGE_TOLERANCE
  )

  async function onDeployIsland() {
    if (!chainId || !account) return
    const tokenA = isNativeCurrencyId(currencyIdA, chainId) ? baseCurrency?.wrapped.address : currencyIdA
    const tokenB = isNativeCurrencyId(currencyIdB, chainId) ? baseCurrency?.wrapped.address : currencyIdB

    if (!tokenA || !tokenB || !feeAmount || !ticks.LOWER || !ticks.UPPER) return

    const tx = deployVault({
      tokenA,
      tokenB,
      uniFee: feeAmount,
      lowerTick: ticks.LOWER,
      upperTick: ticks.UPPER,
      managerFee: 0,
      manager: ZERO_ADDRESS,
      managerTreasury: ZERO_ADDRESS,
    })

    return tx
  }

  const onAdd = useCallback(async () => {
    // Set attempting state immediately to show loading
    setTransactionState({
      attemptingTxn: true,
      errorMessage: undefined,
      txHash: undefined,
      transactionWaiting: false,
    })

    try {
      if (!chainId || !wagmiPublicClient || !account) {
        throw new Error('Missing wallet connection')
      }

      if (!baseCurrencyWithPreference || !quoteCurrencyWithPreference) {
        throw new Error('Missing currencies')
      }

      if (!position || !deadline) {
        throw new Error('Missing position or deadline')
      }

      const useNative = baseCurrencyWithPreference.isNative
        ? baseCurrencyWithPreference
        : quoteCurrencyWithPreference.isNative
        ? quoteCurrencyWithPreference
        : undefined
      const { calldata, value } =
        hasExistingPosition && tokenId
          ? NonfungiblePositionManager.addCallParameters(position, {
              tokenId,
              slippageTolerance: allowedSlippage,
              deadline: deadline.toString(),
              useNative,
            })
          : NonfungiblePositionManager.addCallParameters(position, {
              slippageTolerance: allowedSlippage,
              recipient: account,
              deadline: deadline.toString(),
              useNative,
              createPool: noLiquidity,
            })

      const txn: { to: string; data: string; value: string } = {
        to: NONFUNGIBLE_POSITION_MANAGER_ADDRESSES[chainId],
        data: calldata,
        value,
      }

      // Estimate gas using wagmi public client
      const estimate = await wagmiPublicClient.estimateGas({
        account,
        to: txn.to as Address,
        data: txn.data as Hex,
        value: BigInt(txn.value),
      })

      // Execute the transaction using wagmi
      const hash = await sendTransactionAsync({
        to: txn.to as Address,
        data: txn.data as Hex,
        gas: calculateGasMargin(estimate as bigint),
        value: BigInt(txn.value),
      })

      setTransactionState({
        attemptingTxn: false,
        errorMessage: undefined,
        txHash: hash,
        transactionWaiting: true,
      })

      addTransaction(
        { hash },
        {
          type: TransactionType.ADD_LIQUIDITY_V3_POOL,
          baseCurrencyId: currencyId(baseCurrencyWithPreference),
          quoteCurrencyId: currencyId(quoteCurrencyWithPreference),
          createPool: Boolean(noLiquidity),
          expectedAmountBaseRaw: parsedAmounts[Field.CURRENCY_A]?.quotient?.toString() ?? '0',
          expectedAmountQuoteRaw: parsedAmounts[Field.CURRENCY_B]?.quotient?.toString() ?? '0',
          feeAmount: position.pool.fee,
        }
      )

      const receipt = await waitForTransactionReceipt({ hash })

      setTransactionState({
        attemptingTxn: false,
        errorMessage: undefined,
        txHash: receipt.transactionHash,
        transactionWaiting: false,
      })

      // Invalidate RTK Query tags after successful transaction
      if (account) {
        await queryClient.invalidateQueries({ queryKey: ['v3'] })

        if (hasExistingPosition && tokenId) {
          await queryClient.invalidateQueries({
            queryKey: v3QueryKeys.positionById(chainId, tokenId.toLowerCase()),
          })
        }
      }
    } catch (error: any) {
      const errorMessage = checkErrorMessage(error, 'Add liquidity failed')

      setTransactionState({
        attemptingTxn: false,
        errorMessage,
        txHash: undefined,
        transactionWaiting: false,
      })

      console.error('Add V3 liquidity error:', error)
    }
  }, [
    setTransactionState,
    chainId,
    wagmiPublicClient,
    account,
    baseCurrencyWithPreference,
    quoteCurrencyWithPreference,
    position,
    deadline,
    hasExistingPosition,
    tokenId,
    allowedSlippage,
    noLiquidity,
    sendTransactionAsync,
    waitForTransactionReceipt,
    addTransaction,
    parsedAmounts,
    dispatch,
  ])

  const handleCurrencySelect = useCallback(
    (currencyNew: Currency, currencyIdOther?: string): (string | undefined)[] => {
      const currencyIdNew = currencyId(currencyNew, chainId)

      if (currencyIdNew === currencyIdOther) {
        // not ideal, but for now clobber the other if the currency ids are equal
        return [currencyIdNew, undefined]
      } else {
        // prevent weth + eth
        const isETHOrWETHNew =
          isNativeCurrencyId(currencyIdNew, chainId) ||
          (chainId !== undefined && currencyIdNew === WRAPPED_NATIVE_CURRENCY[chainId as SupportedChainId]?.address)
        const isETHOrWETHOther =
          currencyIdOther !== undefined &&
          (isNativeCurrencyId(currencyIdOther, chainId) ||
            (chainId !== undefined &&
              currencyIdOther === WRAPPED_NATIVE_CURRENCY[chainId as SupportedChainId]?.address))

        if (isETHOrWETHNew && isETHOrWETHOther) {
          return [currencyIdNew, undefined]
        } else {
          return [currencyIdNew, currencyIdOther]
        }
      }
    },
    [chainId]
  )

  const handleCurrencyASelect = useCallback(
    (currencyANew: Currency) => {
      const [idA, idB] = handleCurrencySelect(currencyANew, quoteCurrencyId)
      if (idB === undefined) {
        navigate(`/liquidity/v3/add/${idA}`)
      } else {
        navigate(`/liquidity/v3/add/${idA}/${idB}`)
      }
    },
    [handleCurrencySelect, quoteCurrencyId, navigate]
  )

  const handleCurrencyBSelect = useCallback(
    (currencyBNew: Currency) => {
      const [idB, idA] = handleCurrencySelect(currencyBNew, baseCurrencyId)
      if (idA === undefined) {
        navigate(`/liquidity/v3/add/${idB}`)
      } else {
        navigate(`/liquidity/v3/add/${idA}/${idB}`)
      }
    },
    [handleCurrencySelect, baseCurrencyId, navigate]
  )

  const handleFeePoolSelect = useCallback(
    (newFeeAmount: FeeAmount) => {
      if (newFeeAmount === feeAmount) {
        // ignore re-select of the same tier
        return
      }

      onLeftRangeInput('')
      onRightRangeInput('')
      onLeftRangeTickInput(undefined)
      onRightRangeTickInput(undefined)
      dispatch(setPresetRange({ preset: PresetRange.WIDE }))
      navigate(`/liquidity/v3/add/${currencyIdA}/${currencyIdB}/${newFeeAmount}`)
    },
    [
      currencyIdA,
      currencyIdB,
      dispatch,
      feeAmount,
      navigate,
      onLeftRangeInput,
      onLeftRangeTickInput,
      onRightRangeInput,
      onRightRangeTickInput,
    ]
  )

  const handleDismissConfirmation = useCallback(() => {
    setShowConfirm(false)
    // if there was a tx hash, we want to clear the input
    if (transactionState.txHash) {
      onFieldAInput('')
      // dont jump to pool page if creating
      navigate('/liquidity/v3')
    }
    setTransactionState({
      attemptingTxn: false,
      errorMessage: undefined,
      txHash: undefined,
    })
  }, [navigate, onFieldAInput, transactionState.txHash])

  const pendingText = `Supplying ${!depositADisabled ? parsedAmounts[Field.CURRENCY_A]?.toSignificant(6) : ''} ${
    !depositADisabled ? currencies[Field.CURRENCY_A]?.symbol : ''
  } ${!outOfRange ? 'and' : ''} ${!depositBDisabled ? parsedAmounts[Field.CURRENCY_B]?.toSignificant(6) : ''} ${
    !depositBDisabled ? currencies[Field.CURRENCY_B]?.symbol : ''
  }`

  const confirmationContent = useCallback(() => {
    if (transactionState.errorMessage) {
      return <TransactionErrorContent onDismiss={handleDismissConfirmation} message={transactionState.errorMessage} />
    }
    if (transactionState.txHash) {
      return (
        <ConfirmationModalContent
          title={<>Add Liquidity</>}
          onDismiss={handleDismissConfirmation}
          topContent={() => (
            <div>
              <div style={{ textAlign: 'center', padding: '1rem' }}>
                <div style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>Transaction Submitted</div>
                <div style={{ fontSize: '0.875rem', opacity: 0.8 }}>{pendingText}</div>
              </div>
            </div>
          )}
          bottomContent={() => null}
        />
      )
    }
    return (
      <ConfirmationModalContent
        title={<>Add Liquidity</>}
        onDismiss={handleDismissConfirmation}
        topContent={() => (
          <Review
            parsedAmounts={parsedAmounts}
            position={position}
            existingPosition={existingPosition}
            priceLower={pricesAtTicks[Bound.LOWER]}
            priceUpper={pricesAtTicks[Bound.UPPER]}
            outOfRange={outOfRange}
            ticksAtLimit={ticksAtLimit}
            baseCurrencyDefault={baseCurrencyWithPreference ?? undefined}
            unwrapNative={!receiveWETH}
          />
        )}
        bottomContent={() => (
          <ButtonPrimary style={{ marginTop: '1rem' }} onClick={onAdd}>
            <Text fontWeight={500} fontSize={20}>
              <>Add</>
            </Text>
          </ButtonPrimary>
        )}
      />
    )
  }, [
    transactionState.errorMessage,
    transactionState.txHash,
    handleDismissConfirmation,
    parsedAmounts,
    position,
    existingPosition,
    pricesAtTicks,
    outOfRange,
    ticksAtLimit,
    onAdd,
    baseCurrencyWithPreference,
    pendingText,
  ])

  const addIsUnsupported = useIsSwapUnsupported(currencies?.CURRENCY_A, currencies?.CURRENCY_B)

  const clearAll = useCallback(() => {
    onFieldAInput('')
    onFieldBInput('')
    onLeftRangeInput('')
    onRightRangeInput('')
    onLeftRangeTickInput(undefined)
    onRightRangeTickInput(undefined)

    dispatch(setPresetRange({ preset: PresetRange.WIDE }))

    navigate('/liquidity/v3/add', { replace: true })
  }, [
    navigate,
    onFieldAInput,
    onFieldBInput,
    onLeftRangeInput,
    onRightRangeInput,
    onLeftRangeTickInput,
    onRightRangeTickInput,
    dispatch,
  ])

  // get value and prices at ticks
  const { [Bound.LOWER]: tickLower, [Bound.UPPER]: tickUpper } = ticks
  const { [Bound.LOWER]: priceLower, [Bound.UPPER]: priceUpper } = pricesAtTicks

  const { getDecrementLower, getIncrementLower, getDecrementUpper, getIncrementUpper, getSetFullRange } =
    useRangeHopCallbacks(
      baseCurrencyWithPreference,
      quoteCurrencyWithPreference,
      feeAmount,
      ticks[Bound.LOWER],
      ticks[Bound.UPPER],
      pool,
      invertPrice
    )
  const wrappedNativeAddress = nativeCurrency.wrapped.address.toLowerCase()
  const canSelectNativeForCurrencyA =
    currencies[Field.CURRENCY_A]?.wrapped?.address?.toLowerCase() === wrappedNativeAddress
  const canSelectNativeForCurrencyB =
    currencies[Field.CURRENCY_B]?.wrapped?.address?.toLowerCase() === wrappedNativeAddress
  const handleNativePreferenceSelect = useCallback((currency: Currency) => {
    setReceiveWETH(!currency.isNative)
  }, [])

  // we need an existence check on parsed amounts for single-asset deposits
  const showApprovalA = approvalA !== ApprovalState.APPROVED && !!parsedAmounts[Field.CURRENCY_A]
  const showApprovalB = approvalB !== ApprovalState.APPROVED && !!parsedAmounts[Field.CURRENCY_B]

  const Buttons = () =>
    addIsUnsupported ? (
      <ButtonPrimary disabled={true} $borderRadius="12px" padding="12px">
        <ThemedText.DeprecatedMain mb="4px">
          <>Unsupported Asset</>
        </ThemedText.DeprecatedMain>
      </ButtonPrimary>
    ) : !account ? (
      <ButtonLight onClick={toggleWalletModal} $borderRadius="12px" padding="12px">
        <>Connect Wallet</>
      </ButtonLight>
    ) : (
      <AutoColumn gap="md">
        {(approvalA === ApprovalState.NOT_APPROVED ||
          approvalA === ApprovalState.PENDING ||
          approvalB === ApprovalState.NOT_APPROVED ||
          approvalB === ApprovalState.PENDING) &&
          isValid && (
            <RowBetween>
              {showApprovalA && (
                <ButtonPrimary
                  onClick={approveACallback}
                  disabled={approvalA === ApprovalState.PENDING}
                  width={showApprovalB ? '48%' : '100%'}
                  nowrap
                >
                  {approvalA === ApprovalState.PENDING ? (
                    <Dots>
                      <>Approving {currencies[Field.CURRENCY_A]?.symbol}</>
                    </Dots>
                  ) : (
                    <>Approve {currencies[Field.CURRENCY_A]?.symbol}</>
                  )}
                </ButtonPrimary>
              )}
              {showApprovalB && (
                <ButtonPrimary
                  onClick={approveBCallback}
                  disabled={approvalB === ApprovalState.PENDING}
                  width={showApprovalA ? '48%' : '100%'}
                  nowrap
                >
                  {approvalB === ApprovalState.PENDING ? (
                    <Dots>
                      <>Approving {currencies[Field.CURRENCY_B]?.symbol}</>
                    </Dots>
                  ) : (
                    <>Approve {currencies[Field.CURRENCY_B]?.symbol}</>
                  )}
                </ButtonPrimary>
              )}
            </RowBetween>
          )}
        <ButtonError
          onClick={() => {
            setShowConfirm(true)
          }}
          disabled={
            !isValid ||
            (approvalA !== ApprovalState.APPROVED && !depositADisabled) ||
            (approvalB !== ApprovalState.APPROVED && !depositBDisabled)
          }
          error={!isValid && !!parsedAmounts[Field.CURRENCY_A] && !!parsedAmounts[Field.CURRENCY_B]}
        >
          <Text fontWeight={500}>{errorMessage ? errorMessage : <>Preview</>}</Text>
        </ButtonError>
      </AutoColumn>
    )

  if (isCheckingExisting) {
    return (
      <ScrollablePage>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
          <Loader size="40px" />
        </div>
      </ScrollablePage>
    )
  }

  return (
    <>
      <ScrollablePage>
        <TransactionConfirmationModal
          isOpen={showConfirm}
          onDismiss={handleDismissConfirmation}
          attemptingTxn={transactionState.attemptingTxn}
          hash={transactionState.txHash || ''}
          transactionWaiting={transactionState.transactionWaiting}
          content={confirmationContent}
          pendingText={pendingText}
        />
        <PageWrapper wide={!hasExistingPosition}>
          <AddRemoveTabs
            creating={false}
            adding={true}
            positionID={undefined}
            defaultSlippage={DEFAULT_ADD_IN_RANGE_SLIPPAGE_TOLERANCE}
            showBackLink={!hasExistingPosition}
          >
            {!hasExistingPosition && (
              <Row justifyContent="flex-end" style={{ width: 'fit-content', minWidth: 'fit-content' }}>
                <MediumOnly>
                  <ButtonText onClick={clearAll} margin="0 15px 0 0">
                    <ThemedText.DeprecatedBlue fontSize="12px">
                      <>Clear All</>
                    </ThemedText.DeprecatedBlue>
                  </ButtonText>
                </MediumOnly>
                {baseCurrencyWithPreference && quoteCurrencyWithPreference ? (
                  <RateToggle
                    currencyA={baseCurrencyWithPreference}
                    currencyB={quoteCurrencyWithPreference}
                    handleRateToggle={() => {
                      if (selectedPreset === PresetRange.FULL) {
                        getSetFullRange()
                      }
                      onFieldAInput(formattedAmounts[Field.CURRENCY_B] ?? '')
                      navigate(`/liquidity/v3/add/${currencyIdB}/${currencyIdA}${feeAmount ? '/' + feeAmount : ''}`)
                    }}
                  />
                ) : null}
              </Row>
            )}
          </AddRemoveTabs>
          <Wrapper wide={!hasExistingPosition}>
            <ResponsiveTwoColumns wide={!hasExistingPosition}>
              <AutoColumn gap="lg">
                {!hasExistingPosition && (
                  <>
                    <SelectPairFlex>
                      {/* <RowBetween paddingBottom="20px"> */}
                      <ThemedText.DeprecatedLabel>
                        <>Select Pair</>
                      </ThemedText.DeprecatedLabel>
                      {/* </RowBetween> */}
                      <RowBetween>
                        <CurrencyDropdown
                          value={formattedAmounts[Field.CURRENCY_A]}
                          hideInput={true}
                          onUserInput={onFieldAInput}
                          onCurrencySelect={handleCurrencyASelect}
                          onMax={() => {
                            onFieldAInput(maxAmounts[Field.CURRENCY_A]?.toExact() ?? '')
                          }}
                          showMaxButton={!atMaxAmounts[Field.CURRENCY_A]}
                          currency={currencies[Field.CURRENCY_A] ?? null}
                          id="add-liquidity-input-tokena"
                          showCommonBases
                        />

                        <div style={{ width: '12px' }} />

                        <CurrencyDropdown
                          value={formattedAmounts[Field.CURRENCY_B]}
                          hideInput={true}
                          onUserInput={onFieldBInput}
                          onCurrencySelect={handleCurrencyBSelect}
                          onMax={() => {
                            onFieldBInput(maxAmounts[Field.CURRENCY_B]?.toExact() ?? '')
                          }}
                          showMaxButton={!atMaxAmounts[Field.CURRENCY_B]}
                          currency={currencies[Field.CURRENCY_B] ?? null}
                          id="add-liquidity-input-tokenb"
                          showCommonBases
                        />
                      </RowBetween>

                      <FeeSelector
                        disabled={!quoteCurrencyWithPreference || !baseCurrencyWithPreference}
                        feeAmount={feeAmount}
                        handleFeePoolSelect={handleFeePoolSelect}
                        currencyA={baseCurrencyWithPreference}
                        currencyB={quoteCurrencyWithPreference}
                      />
                    </SelectPairFlex>{' '}
                  </>
                )}
                {hasExistingPosition && existingPosition && (
                  <PositionPreview
                    position={existingPosition}
                    title={<>Selected Range</>}
                    inRange={!outOfRange}
                    ticksAtLimit={ticksAtLimit}
                  />
                )}
              </AutoColumn>
              <div>
                <DynamicSection
                  disabled={tickLower === undefined || tickUpper === undefined || invalidPool || invalidRange}
                  style={{ marginTop: '4px' }}
                >
                  <AutoColumn gap="md">
                    <ThemedText.DeprecatedLabel>
                      {hasExistingPosition ? <>Add more liquidity</> : <>Deposit Amounts</>}
                    </ThemedText.DeprecatedLabel>

                    <CurrencyInputPanel
                      value={formattedAmounts[Field.CURRENCY_A]}
                      onUserInput={onFieldAInput}
                      onMax={() => {
                        onFieldAInput(maxAmounts[Field.CURRENCY_A]?.toExact() ?? '')
                      }}
                      showMaxButton={!atMaxAmounts[Field.CURRENCY_A]}
                      currency={currencies[Field.CURRENCY_A] ?? null}
                      id="add-liquidity-input-tokena"
                      apiUSDValue={usdcValues[Field.CURRENCY_A]?.toString() ?? undefined}
                      locked={depositADisabled}
                      onCurrencySelect={canSelectNativeForCurrencyA ? handleNativePreferenceSelect : undefined}
                      allowedCurrencies={canSelectNativeForCurrencyA ? [nativeCurrency.wrapped.address] : undefined}
                      showCategories={false}
                      compactSearchModal
                    />

                    <CurrencyInputPanel
                      value={formattedAmounts[Field.CURRENCY_B]}
                      onUserInput={onFieldBInput}
                      onMax={() => {
                        onFieldBInput(maxAmounts[Field.CURRENCY_B]?.toExact() ?? '')
                      }}
                      showMaxButton={!atMaxAmounts[Field.CURRENCY_B]}
                      currency={currencies[Field.CURRENCY_B] ?? null}
                      id="add-liquidity-input-tokenb"
                      apiUSDValue={usdcValues[Field.CURRENCY_B]?.toString() ?? ''}
                      showCommonBases
                      locked={depositBDisabled}
                      onCurrencySelect={canSelectNativeForCurrencyB ? handleNativePreferenceSelect : undefined}
                      allowedCurrencies={canSelectNativeForCurrencyB ? [nativeCurrency.wrapped.address] : undefined}
                      showCategories={false}
                      compactSearchModal
                    />
                    <AdvancedDeploySection
                      priceLower={priceLower}
                      priceUpper={priceUpper}
                      currencyA={baseCurrencyWithPreference}
                      currencyB={quoteCurrencyWithPreference}
                      ticks={ticks}
                      isNoLiquidity={noLiquidity}
                      ticksAtLimit={ticksAtLimit}
                      onDeployIsland={onDeployIsland}
                    />
                  </AutoColumn>
                </DynamicSection>
              </div>

              {!hasExistingPosition ? (
                <>
                  <HideMedium>
                    <Buttons />
                  </HideMedium>
                  <RightContainer gap="lg">
                    <DynamicSection gap="md" disabled={!feeAmount || invalidPool}>
                      {!noLiquidity ? (
                        <>
                          <RowBetween>
                            <ThemedText.DeprecatedLabel>
                              <>Set Price Range</>
                            </ThemedText.DeprecatedLabel>
                          </RowBetween>

                          {price && baseCurrencyWithPreference && quoteCurrencyWithPreference && !noLiquidity && (
                            <AutoRow gap="4px" justify="center" style={{ marginTop: '0.5rem' }}>
                              <>
                                <ThemedText.DeprecatedMain
                                  fontWeight={500}
                                  textAlign="center"
                                  fontSize={12}
                                  color="text1"
                                >
                                  Current Price:
                                </ThemedText.DeprecatedMain>
                                <ThemedText.DeprecatedBody
                                  fontWeight={500}
                                  textAlign="center"
                                  fontSize={12}
                                  color="text1"
                                >
                                  <HoverInlineText
                                    maxCharacters={20}
                                    text={invertPrice ? price.invert().toSignificant(8) : price.toSignificant(8)}
                                  />
                                </ThemedText.DeprecatedBody>
                                <ThemedText.DeprecatedBody color="text2" fontSize={12}>
                                  {quoteCurrencyWithPreference?.symbol} per {baseCurrencyWithPreference.symbol}
                                </ThemedText.DeprecatedBody>
                              </>
                            </AutoRow>
                          )}

                          <LiquidityChartRangeInput
                            currencyA={baseCurrencyWithPreference}
                            currencyB={quoteCurrencyWithPreference}
                            feeAmount={feeAmount}
                            ticksAtLimit={ticksAtLimit}
                            price={
                              price ? parseFloat((invertPrice ? price.invert() : price).toSignificant(8)) : undefined
                            }
                            priceLower={priceLower}
                            priceUpper={priceUpper}
                            onLeftRangeInput={onLeftRangeInput}
                            onRightRangeInput={onRightRangeInput}
                            interactive={!hasExistingPosition}
                          />
                        </>
                      ) : (
                        <AutoColumn gap="md">
                          <RowBetween>
                            <ThemedText.DeprecatedLabel fontSize={14} fontWeight={500}>
                              <>Set Starting Price</>
                            </ThemedText.DeprecatedLabel>
                          </RowBetween>
                          {noLiquidity && (
                            <BlueCard
                              style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                padding: '8px',
                                borderRadius: '8px',
                                border: '1px solid',
                                borderColor: theme.cardBorderPrimary,
                              }}
                            >
                              <ThemedText.DeprecatedBody
                                fontSize={14}
                                style={{ fontWeight: 500 }}
                                textAlign="left"
                                color={theme.deprecated_primaryText1}
                              >
                                <>
                                  This pool must be initialized before you can add liquidity. To initialize, select a
                                  starting price for the pool. Then, enter your liquidity price range and deposit
                                  amount.
                                </>
                              </ThemedText.DeprecatedBody>
                            </BlueCard>
                          )}
                          <StyledOutlineCard padding="12px">
                            <StyledInput
                              className="start-price-input"
                              value={startPriceTypedValue}
                              onUserInput={onStartPriceInput}
                            />
                          </StyledOutlineCard>
                          <RowBetween
                            style={{ backgroundColor: theme.cardSecondary, padding: '12px', borderRadius: '8px' }}
                          >
                            <ThemedText.DeprecatedMain>
                              <>Current {baseCurrencyWithPreference?.symbol} Price:</>
                            </ThemedText.DeprecatedMain>
                            <ThemedText.DeprecatedMain>
                              {price ? (
                                <ThemedText.DeprecatedMain>
                                  <RowFixed>
                                    <HoverInlineText
                                      maxCharacters={20}
                                      text={invertPrice ? price?.invert()?.toSignificant(5) : price?.toSignificant(5)}
                                    />{' '}
                                    <span style={{ marginLeft: '4px' }}>{quoteCurrencyWithPreference?.symbol}</span>
                                  </RowFixed>
                                </ThemedText.DeprecatedMain>
                              ) : (
                                '-'
                              )}
                            </ThemedText.DeprecatedMain>
                          </RowBetween>
                        </AutoColumn>
                      )}
                    </DynamicSection>

                    <DynamicSection
                      gap="md"
                      disabled={!feeAmount || invalidPool || (noLiquidity && !startPriceTypedValue)}
                    >
                      <StackedContainer>
                        <StackedItem style={{ opacity: showCapitalEfficiencyWarning ? '0.05' : 1 }}>
                          <AutoColumn gap="md">
                            {noLiquidity && (
                              <RowBetween>
                                <ThemedText.DeprecatedLabel>
                                  <>Set Price Range</>
                                </ThemedText.DeprecatedLabel>
                              </RowBetween>
                            )}
                            <RangeSelector
                              priceLower={pricesAtTicks[Bound.LOWER]}
                              priceUpper={pricesAtTicks[Bound.UPPER]}
                              getDecrementLower={getDecrementLower}
                              getIncrementLower={getIncrementLower}
                              getDecrementUpper={getDecrementUpper}
                              getIncrementUpper={getIncrementUpper}
                              onLeftRangeInput={onLeftRangeInput}
                              onRightRangeInput={onRightRangeInput}
                              currencyA={baseCurrencyWithPreference}
                              currencyB={quoteCurrencyWithPreference}
                              feeAmount={feeAmount}
                              ticksAtLimit={ticksAtLimit}
                              tickLower={ticks[Bound.LOWER]}
                              tickUpper={ticks[Bound.UPPER]}
                              onLeftRangeTickInput={onLeftRangeTickInput}
                              onRightRangeTickInput={onRightRangeTickInput}
                              invertPrice={invertPrice}
                              // inputsDisabled={!isCustomRange}
                            />
                            {!noLiquidity && currencyIdA && currencyIdB && (
                              <PresetsButtons
                                selectedPreset={selectedPreset}
                                onPresetSelect={handlePresetSelect}
                                disabled={!feeAmount || invalidPool}
                              />
                            )}
                          </AutoColumn>
                        </StackedItem>

                        {showCapitalEfficiencyWarning && (
                          <StackedItem zIndex={1}>
                            <YellowCard
                              padding="15px"
                              $borderRadius="12px"
                              height="100%"
                              style={{
                                borderColor: theme.deprecated_yellow3,
                                border: '1px solid',
                              }}
                            >
                              <AutoColumn gap="8px" style={{ height: '100%' }}>
                                <RowFixed>
                                  <AlertTriangle stroke={theme.deprecated_yellow3} size="16px" />
                                  <ThemedText.DeprecatedYellow ml="12px" fontSize="15px">
                                    <>Efficiency Comparison</>
                                  </ThemedText.DeprecatedYellow>
                                </RowFixed>
                                <RowFixed>
                                  <ThemedText.DeprecatedYellow ml="12px" fontSize="13px" margin={0} fontWeight={400}>
                                    <>Full range positions may earn less fees than concentrated positions.</>
                                  </ThemedText.DeprecatedYellow>
                                </RowFixed>
                                <Row>
                                  <ButtonYellow
                                    padding="8px"
                                    marginRight="8px"
                                    $borderRadius="8px"
                                    width="auto"
                                    onClick={() => {
                                      setShowCapitalEfficiencyWarning(false)
                                      getSetFullRange()
                                    }}
                                  >
                                    <ThemedText.DeprecatedBlack fontSize={13} color="black">
                                      <>I understand</>
                                    </ThemedText.DeprecatedBlack>
                                  </ButtonYellow>
                                </Row>
                              </AutoColumn>
                            </YellowCard>
                          </StackedItem>
                        )}
                      </StackedContainer>

                      {outOfRange ? (
                        <YellowCard padding="8px 12px" $borderRadius="12px">
                          <RowBetween>
                            <AlertTriangle stroke={theme.deprecated_yellow3} size="16px" />
                            <ThemedText.DeprecatedYellow ml="12px" fontSize="12px">
                              <>
                                Your position will not earn fees or be used in trades until the market price moves into
                                your range.
                              </>
                            </ThemedText.DeprecatedYellow>
                          </RowBetween>
                        </YellowCard>
                      ) : null}

                      {invalidRange ? (
                        <YellowCard padding="8px 12px" $borderRadius="12px">
                          <RowBetween>
                            <AlertTriangle stroke={theme.deprecated_yellow3} size="16px" />
                            <ThemedText.DeprecatedYellow ml="12px" fontSize="12px">
                              <>Invalid range selected. The min price must be lower than the max price.</>
                            </ThemedText.DeprecatedYellow>
                          </RowBetween>
                        </YellowCard>
                      ) : null}
                    </DynamicSection>

                    <MediumOnly>
                      <Buttons />
                    </MediumOnly>
                  </RightContainer>
                </>
              ) : (
                <Buttons />
              )}
            </ResponsiveTwoColumns>
          </Wrapper>
        </PageWrapper>
        {addIsUnsupported && (
          <UnsupportedCurrencyFooter
            show={addIsUnsupported}
            currencies={[currencies.CURRENCY_A, currencies.CURRENCY_B]}
          />
        )}
        <PoweredByKodiak />
      </ScrollablePage>
      <SwitchLocaleLink />
    </>
  )
}
